'use client';

import * as React from 'react';
import Link from 'next/link';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DropdownItem {
  title: string;
  href: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface DropdownMenuProps {
  title: string;
  href: string;
  items: DropdownItem[];
  isActive?: boolean;
}

export function DropdownMenu({ title, href, items, isActive = false }: DropdownMenuProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Link
        href={href}
        className={cn(
          "flex items-center gap-1 px-3 py-2 text-sm font-medium",
          "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white",
          "transition-colors duration-200",
          isOpen && "text-gray-900 dark:text-white"
        )}
      >
        {title}
        <ChevronDown
          className={cn(
            "h-4 w-4 transition-transform duration-200",
            isOpen && "rotate-180"
          )}
        />
      </Link>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute left-0 top-full mt-2 w-64 bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-800 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
          {items.map((item, index) => {
            const ItemIcon = item.icon || ArrowRight;
            const isLastItem = index === items.length - 1;
            return (
              <Link
                key={index}
                href={item.href}
                  className={cn(
                    "flex items-center gap-3 px-4 py-2.5 text-sm",
                    isLastItem 
                      ? "border-t border-gray-200 dark:border-gray-800 mt-1 pt-3 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-semibold hover:bg-primary-100 dark:hover:bg-primary-900/30"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white",
                    "transition-all duration-200 group/item"
                  )}
                style={{
                  animationDelay: `${index * 30}ms`,
                }}
              >
                <ItemIcon className={cn(
                  "h-4 w-4 transition-colors duration-200",
                  isLastItem
                    ? "text-primary-600 dark:text-primary-400 group-hover/item:text-primary-700 dark:group-hover/item:text-primary-300"
                    : "text-gray-500 dark:text-gray-400 group-hover/item:text-primary-600 dark:group-hover/item:text-primary-400"
                )} />
                <span className="flex-1">{item.title}</span>
                <ArrowRight className={cn(
                  "h-3 w-3 transition-all duration-200",
                  isLastItem
                    ? "opacity-100 translate-x-0 text-primary-600 dark:text-primary-400"
                    : "opacity-0 group-hover/item:opacity-100 -translate-x-2 group-hover/item:translate-x-0"
                )} />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

